import { formatRub } from '@/pages/admin/ui/adminFormat';
export const ADMIN_CHART_KINDS = [
    { id: 'line', label: 'Линия' },
    { id: 'area', label: 'Область' },
    { id: 'bar', label: 'Столбцы' },
];
export const ADMIN_CHART_METRICS = [
    { id: 'turnover', label: 'Оборот' },
    { id: 'orders', label: 'Заказы' },
    { id: 'avgCheck', label: 'Средний чек' },
    { id: 'turnoverAndOrders', label: 'Оборот и заказы' },
];
const COLORS = ['#4da2f1', '#ff3d64', '#8ad554', '#ffc636', '#ff8a3d', '#59d6c5', '#b57bff', '#6b6f85'];
export function metricPointValue(point, metric) {
    if (metric === 'turnover')
        return point.turnoverRub;
    if (metric === 'orders')
        return point.ordersCount;
    return point.ordersCount > 0 ? point.turnoverRub / point.ordersCount : 0;
}
export function collectBuckets(series) {
    return Array.from(new Set(series.flatMap((item) => item.points.map((p) => p.bucketStart)))).sort();
}
export function formatChartBucket(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime()))
        return value.slice(0, 10);
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}
export function formatChartAxis(value, money) {
    if (!Number.isFinite(value))
        return '';
    if (Math.abs(value) >= 1000000) {
        return `${(value / 1000000).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} млн`;
    }
    if (Math.abs(value) >= 1000) {
        return `${Math.round(value / 1000).toLocaleString('ru-RU')} тыс`;
    }
    return money ? formatRub(value) : value.toLocaleString('ru-RU');
}
function colorAt(index) {
    return COLORS[index % COLORS.length];
}
function valuesByBucket(item, buckets, metric) {
    const map = new Map(item.points.map((p) => [p.bucketStart, metricPointValue(p, metric)]));
    return buckets.map((bucket) => map.get(bucket) ?? 0);
}
function seriesType(kind, asLine) {
    if (asLine)
        return 'line';
    return kind === 'bar' ? 'bar' : 'line';
}
function baseLineStyle(kind, color, asLine) {
    const type = seriesType(kind, asLine);
    return {
        type,
        color,
        showSymbol: false,
        smooth: false,
        ...(type === 'line' && kind === 'area' && !asLine ? { areaStyle: { opacity: 0.16, color } } : {}),
        ...(type === 'bar' ? { barMaxWidth: 28 } : {}),
        lineStyle: { width: asLine && kind !== 'line' ? 2 : 2.25, color },
        z: asLine ? 3 : 1,
        emphasis: { focus: 'none' },
    };
}
export function buildAdminMetricsChartOption(series, kind, metric) {
    const buckets = collectBuckets(series);
    const labels = buckets.map(formatChartBucket);
    const dual = metric === 'turnoverAndOrders';
    const chartSeries = [];
    series.forEach((item, index) => {
        const color = colorAt(index);
        if (dual) {
            chartSeries.push({
                ...baseLineStyle(kind, color, false),
                name: `${item.restaurantLabel} - оборот`,
                yAxisIndex: 0,
                data: valuesByBucket(item, buckets, 'turnover'),
            });
            chartSeries.push({
                ...baseLineStyle(kind, color, true),
                name: `${item.restaurantLabel} - заказы`,
                yAxisIndex: 1,
                data: valuesByBucket(item, buckets, 'orders'),
            });
            return;
        }
        chartSeries.push({
            ...baseLineStyle(kind, color, false),
            name: item.restaurantLabel,
            yAxisIndex: 0,
            data: valuesByBucket(item, buckets, metric),
        });
    });
    const moneyLeft = metric !== 'orders';
    const leftName = metric === 'orders' ? 'Заказы' : metric === 'avgCheck' ? 'Средний чек, ₽' : 'Оборот, ₽';
    return {
        color: COLORS,
        animationDuration: 250,
        grid: { left: 12, right: dual ? 12 : 8, top: 12, bottom: 36, containLabel: true },
        legend: {
            type: 'scroll',
            bottom: 0,
            icon: 'circle',
            itemWidth: 8,
            itemHeight: 8,
            selectedMode: false,
            textStyle: { color: '#4e3f3a', fontSize: 12 },
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: kind === 'bar' && !dual ? 'shadow' : 'line' },
            backgroundColor: '#fff',
            borderColor: '#e8e4df',
            borderWidth: 1,
            padding: [10, 12],
            textStyle: { color: '#4e3f3a', fontSize: 12 },
            extraCssText: 'box-shadow: 0 8px 24px rgba(78, 63, 58, 0.12); border-radius: 8px;',
            formatter: (raw) => formatAxisTooltip(raw, metric),
        },
        xAxis: {
            type: 'category',
            data: labels,
            boundaryGap: kind === 'bar',
            axisTick: { show: false },
            axisLine: { lineStyle: { color: '#e8e4df' } },
            axisLabel: { color: '#6b5d58', fontSize: 11, hideOverlap: true },
        },
        yAxis: dual
            ? [
                axisConfig(leftName, true),
                axisConfig('Заказы', false),
            ]
            : axisConfig(leftName, moneyLeft),
        series: chartSeries,
    };
}
function axisConfig(name, money) {
    return {
        type: 'value',
        name,
        nameTextStyle: { color: '#6b5d58', fontSize: 11, padding: [0, 0, 0, 8] },
        splitLine: { lineStyle: { color: '#f0ece6', type: 'dashed' } },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
            color: '#6b5d58',
            fontSize: 11,
            formatter: (value) => formatChartAxis(value, money),
        },
    };
}
function formatAxisTooltip(raw, metric) {
    const items = Array.isArray(raw) ? raw : [];
    if (items.length === 0)
        return '';
    const first = items[0];
    const rows = items
        .map((item) => {
        const row = item;
        const value = Number(row.value ?? 0);
        const money = tooltipIsMoney(metric, row.seriesName ?? '');
        return `<div style="display:flex;justify-content:space-between;gap:1.5rem;margin-top:4px">
        <span><span style="display:inline-block;width:8px;height:8px;border-radius:99px;background:${row.color};margin-right:6px"></span>${escapeHtml(row.seriesName ?? '')}</span>
        <strong>${money ? formatRub(value) : value.toLocaleString('ru-RU')}</strong>
      </div>`;
    })
        .join('');
    return `<div><div style="color:#6b5d58;margin-bottom:4px">${escapeHtml(String(first.axisValue ?? ''))}</div>${rows}</div>`;
}
function tooltipIsMoney(metric, seriesName) {
    if (metric === 'orders')
        return false;
    if (metric === 'turnoverAndOrders')
        return seriesName.endsWith('оборот');
    return true;
}
function escapeHtml(value) {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
